import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { RegionListItemDto, RegionDropdownDto, CreateRegionCommand, UpdateRegionCommand } from '../../domain/entities/Region';
import { SubAreaDto } from '../../domain/entities/SubArea';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { UserRole } from '../../domain/value-objects/UserRole';
import { RegionService } from '../services/RegionService';
import { RegionRepository } from '../../infrastructure/repositories/RegionRepository';
import { SubAreaRepository } from '../../infrastructure/repositories/SubAreaRepository';
import { apiClient } from '../../infrastructure/api/ApiClient';
import { notificationService } from '../../infrastructure/notifications';
import { queryKeys } from './queryKeys';
import { CACHE_CONFIG } from './queryClient';

/**
 * Service instance (singleton)
 */
const regionRepository = new RegionRepository(apiClient);
const regionService = new RegionService(regionRepository);
const subAreaRepository = new SubAreaRepository(apiClient);

/**
 * Create region mutation params
 */
interface CreateRegionParams {
  command: CreateRegionCommand;
  userRole: UserRole;
}

/**
 * Update region mutation params
 */
interface UpdateRegionParams {
  id: string;
  command: UpdateRegionCommand;
  userRole: UserRole;
}

/**
 * Delete region mutation params
 */
interface DeleteRegionParams {
  id: string;
  userRole: UserRole;
}

/**
 * Toggle region active status mutation params
 */
interface ToggleRegionActiveParams {
  id: string;
  userRole: UserRole;
}

/**
 * Update subreddits mutation params
 */
interface UpdateSubredditsParams {
  regionId: string;
  subreddits: string[];
}

/**
 * Fetch regions list query hook (admin only)
 *
 * @param params - Pagination parameters
 * @param userRole - Current user's role
 * @param enabled - Whether the query should run (default: true for admins)
 */
export const useRegionsQuery = (
  params?: PaginationParams,
  userRole?: UserRole,
  enabled: boolean = true
): UseQueryResult<PaginatedResult<RegionListItemDto>, Error> => {
  return useQuery({
    queryKey: queryKeys.regions.list(params),
    queryFn: async (): Promise<PaginatedResult<RegionListItemDto>> => {
      return regionService.getAllRegions(params, userRole);
    },
    staleTime: CACHE_CONFIG.REGIONS.staleTime,
    gcTime: CACHE_CONFIG.REGIONS.gcTime,
    enabled: enabled && userRole === 'Admin' // Only admins can fetch regions
  });
};

/**
 * Fetch active regions for dropdown/select components (anonymous, lightweight)
 * Sprint 032: Uses dedicated dropdown endpoint returning only id, regionName, state, country.
 * Cached for 30 minutes (CACHE_CONFIG.REGIONS) since regions rarely change.
 */
export const useRegionDropdownQuery = (): UseQueryResult<RegionDropdownDto[], Error> => {
  return useQuery({
    queryKey: queryKeys.regions.dropdown(),
    queryFn: async (): Promise<RegionDropdownDto[]> => {
      return regionRepository.getDropdownOptions();
    },
    staleTime: CACHE_CONFIG.REGIONS.staleTime,
    gcTime: CACHE_CONFIG.REGIONS.gcTime
  });
};

/**
 * Create region mutation hook (admin only)
 *
 * Creates a new region and invalidates the regions list cache
 */
export const useCreateRegionMutation = (): UseMutationResult<void, Error, CreateRegionParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ command, userRole }: CreateRegionParams): Promise<void> => {
      await regionService.createRegion(command, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all region list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.regions.lists() });

      // Show success notification
      notificationService.showSuccess(
        `Region "${variables.command.name}" has been created successfully`,
        'Region Created'
      );
    },
    onError: (error: Error): void => {
      console.error('Create region failed:', error);
    }
  });
};

/**
 * Update region mutation hook (admin only)
 *
 * Updates an existing region and invalidates related caches
 */
export const useUpdateRegionMutation = (): UseMutationResult<void, Error, UpdateRegionParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, command, userRole }: UpdateRegionParams): Promise<void> => {
      await regionService.updateRegion(id, command, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all region list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.regions.lists() });

      // Invalidate the specific region detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.regions.detail(variables.id) });

      // Show success notification
      notificationService.showSuccess('Region has been updated successfully', 'Region Updated');
    },
    onError: (error: Error): void => {
      console.error('Update region failed:', error);
    }
  });
};

/**
 * Delete region mutation hook (admin only)
 *
 * Permanently deletes a region and removes it from cache
 */
export const useDeleteRegionMutation = (): UseMutationResult<void, Error, DeleteRegionParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userRole }: DeleteRegionParams): Promise<void> => {
      return regionService.deleteRegion(id, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all region list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.regions.lists() });

      // Remove the specific region from cache
      void queryClient.removeQueries({ queryKey: queryKeys.regions.detail(variables.id) });

      // Show success notification
      notificationService.showSuccess('Region has been deleted successfully', 'Region Deleted');
    },
    onError: (error: Error): void => {
      console.error('Delete region failed:', error);
    }
  });
};

/**
 * Toggle region active status mutation hook (admin only)
 *
 * Toggles a region's active status and invalidates related caches
 */
export const useToggleRegionActiveMutation = (): UseMutationResult<void, Error, ToggleRegionActiveParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userRole }: ToggleRegionActiveParams): Promise<void> => {
      await regionService.toggleRegionActive(id, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all region list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.regions.lists() });

      // Invalidate the specific region detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.regions.detail(variables.id) });

      // Show success notification
      notificationService.showSuccess('Region status has been updated successfully', 'Region Status Updated');
    },
    onError: (error: Error): void => {
      console.error('Toggle region active status failed:', error);
    }
  });
};

/**
 * Update subreddits mutation hook (admin only)
 *
 * Updates the list of subreddits for a region and invalidates related caches
 */
export const useUpdateSubredditsMutation = (): UseMutationResult<void, Error, UpdateSubredditsParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ regionId, subreddits }: UpdateSubredditsParams): Promise<void> => {
      await regionRepository.updateSubreddits(regionId, subreddits);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all region list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.regions.lists() });

      // Invalidate the specific region detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.regions.detail(variables.regionId) });

      // Show success notification
      notificationService.showSuccess('Subreddits updated successfully', 'Subreddits Updated');
    },
    onError: (error: Error): void => {
      console.error('Update subreddits failed:', error);
      notificationService.showError(
        error.message || 'Failed to update subreddits',
        'Update Failed'
      );
    }
  });
};

/**
 * Fetch subareas for a specific region query hook
 *
 * @param regionId - Region ID to fetch subareas for
 * @param enabled - Whether the query should run (default: true if regionId is provided)
 */
export const useSubAreasQuery = (
  regionId?: string,
  enabled?: boolean
): UseQueryResult<SubAreaDto[], Error> => {
  const shouldEnable = enabled !== undefined ? enabled : !!regionId;

  return useQuery({
    queryKey: queryKeys.subAreas.byRegion(regionId ?? ''),
    queryFn: async (): Promise<SubAreaDto[]> => {
      if (!regionId) return [];
      return subAreaRepository.getByRegionId(regionId);
    },
    staleTime: CACHE_CONFIG.REGIONS.staleTime,
    gcTime: CACHE_CONFIG.REGIONS.gcTime,
    enabled: shouldEnable
  });
};
